/**
 * Product structure lookups keyed by Sistem 1 (category value).
 * Extend chapters/subchapters per system as Excel sheets are added.
 */
import { MAIN_PRODUCT_CATEGORIES } from '@/data/productCategories';

const IPA_CHAPTERS = {
    '1': {
        name: "Izolatie",
        subchapters: {
            '01': "Panou cu nuturi",
            '02': "Sistem redus",
            '03': "Rola Tacker",
            '04': "Polistiren expandat",
            '05': "Folie",
            '06': "Clipsuri",
            '07': "Sine",
        },
    },
    '2': {
        name: "Teava",
        subchapters: {
            '01': "PE-Xa",
            '02': "PE-Xa in copex",
            '03': "Pex-Al-Pex izolat",
            '04': "Copex",
            '05': "Izolatie teava",
        },
    },
    '3': {
        name: "Distribuitoare",
        subchapters: {
            '01': "Plastic modulare complete, cu debitmetre",
            '02': "Accesorii distribuitoare plastic modulare",
            '04': "Inox complete, cu debitmetre",
            '06': "Inox complete, cu robineti simpli",
            '07': "Inox bara cu robineti simpli",
            '08': "Stantate",
            '10': "Materii prime distribuitoare metalice",
            '11': "Accesorii distribuitoare metalice",
            '12': "Eurocon",
        },
    },
    '4': {
        name: "Reglare temperatura",
        subchapters: {
            '01': "Kit reglare temperatura",
            '02': "Accesorii kit reglare temperatura",
            '03': "Pompe",
            '04': "Butelii de egalizare",
            '05': "Grupuri de pompare",
            '06': "Distribuitoare pentru grupuri de pompare",
            '07': "Vane de amestec",
            '08': "Accesorii vane, butelii, grupuri",
        },
    },
    '5': {
        name: "Accesorii",
        subchapters: {
            '01': "Dilatare",
            '02': "Aditivi",
            '03': "Armare",
            '04': "Clips",
            '05': "Cutii metalice",
            '06': "Cutii plastic",
        },
    },
    '6': {
        name: "Automatizare",
        subchapters: {
            '01': "Servomotoare",
            '02': "Cutii de comanda",
            '03': "Termostate cu fir",
            '04': "Termostate cu RF",
            '05': "Smart house",
            '07': "Amestec",
        },
    },
    '7': {
        name: "Fitinguri",
        subchapters: {
            '01': "Cu sertizare pentru Pex-Al-Pex",
        },
    },
    '14': {
        name: "Consumabile",
        subchapters: {
            '01': "diverse",
        },
    },
    '15': {
        name: "Scule",
        subchapters: {
            '01': "IPA",
            '02': "ISA",
            '03': "ISA",
        },
    },
};

const SANITARE_CHAPTERS = {
    '2': {
        name: "Teava",
        subchapters: {
            '03': "Pex-Al-Pex izolat",
        },
    },
    '7': {
        name: "Fitinguri",
        subchapters: {
            '01': "Cu sertizare pentru Pex-Al-Pex",
            '02': "Q&E",
            '03': "Accesorii ISA",
        },
    },
};

const TAVAN_CHAPTERS = {
    '2': {
        name: "Teava",
        subchapters: {
            '01': "PE-Xa",
            '02': "PE-Xa in copex",
            '03': "Pex-Al-Pex izolat",
            '04': "Copex",
        },
    },
    '11': {
        name: "Tavan Racit",
        subchapters: {
            '01': "Panouri standard tavan",
            '02': "Racorduri rapide tavan",
            '03': "Consumabile",
        },
    },
};

const CT_CHAPTERS = {
    '13': {
        name: "Camera tehnica",
        subchapters: {
            '01': "Alama",
            '02': "Supape",
            '03': "Robineti trecere",
            '04': "Robineti trecere cu olandez",
            '05': "Termometre",
        },
    },
};

const VMC_CHAPTERS = {
    '8': {
        name: "VMC",
        subchapters: {
            '01': "Aspiratoare VMC",
            '02': "VMC descentralizat",
            '03': "Unitati automate",
            '04': "Unitati entalpice",
            '05': "Control VMC",
            '06': "Distribuitoare VMC",
            '07': "Plenum VMC",
            '08': "Gratare VMC",
            '09': "Teava si accesorii VMC",
            '10': "Baterii VMC",
            '11': "Filtre VMC",
            '12': "Neutralizare mirosuri",
        },
    },
};

const ASPIRARE_CHAPTERS = {
    '9': {
        name: "Aspirare",
        subchapters: {
            '01': "Aspiratoare cu display",
            '02': "Aspiratoare fara display",
            '04': "Aspiratoare AP",
            '05': "Doze",
            '06': "Prize",
            '07': "Suporti",
            '08': "Teava si accesorii Aspiratie",
            '09': "Consumabile",
            '10': "Furtune",
            '11': "Set accesorii furtun",
            '13': "Alte accesorii",
            '14': "Consumabile",
            '15': "Pratico",
            '16': "Robo",
        },
    },
};

/** @type {Record<string, { chapters: Record<string, { name: string, subchapters: Record<string, string> }> }>} */
const CHAPTERS_BY_SYSTEM = {
    IPA: IPA_CHAPTERS,
    SANITARE: SANITARE_CHAPTERS,
    TAVAN: TAVAN_CHAPTERS,
    'CAMERA TEHNICA': CT_CHAPTERS,
    VMC: VMC_CHAPTERS,
    ASPIRARE: ASPIRARE_CHAPTERS,
};

const STRUCTURE_BY_SYSTEM = MAIN_PRODUCT_CATEGORIES.reduce((lookup, category) => {
    lookup[category.value] = {
        chapters: CHAPTERS_BY_SYSTEM[category.value] || {},
    };
    for (const alias of category.systemAliases || []) {
        const aliasKey = String(alias).toUpperCase();
        if (aliasKey !== category.value) {
            lookup[aliasKey] = lookup[category.value];
        }
    }
    return lookup;
}, {});

export const PRODUCT_STRUCTURE_BY_SYSTEM = STRUCTURE_BY_SYSTEM;

let runtimeStructureBySystem = null;

/**
 * @returns {typeof PRODUCT_STRUCTURE_BY_SYSTEM}
 */
function getStructureBySystem() {
    return runtimeStructureBySystem || PRODUCT_STRUCTURE_BY_SYSTEM;
}

/**
 * @param {typeof PRODUCT_STRUCTURE_BY_SYSTEM|null} structure
 */
export function setProductStructureBySystem(structure) {
    runtimeStructureBySystem = structure && Object.keys(structure).length
        ? normalizeProductStructureBySystem(structure)
        : null;
}

function normalizeSystemKey(system1) {
    const raw = String(system1 || '').trim().toUpperCase();
    if (!raw) {
        return '';
    }
    const match = MAIN_PRODUCT_CATEGORIES.find((category) =>
        category.value === raw
        || category.systemAliases?.some((alias) => alias.toUpperCase() === raw)
    );
    return match?.value || raw;
}

/**
 * Ensures API-loaded structure keeps CT alias for Camera tehnică.
 * @param {typeof PRODUCT_STRUCTURE_BY_SYSTEM} structure
 * @returns {typeof PRODUCT_STRUCTURE_BY_SYSTEM}
 */
export function normalizeProductStructureBySystem(structure) {
    if (!structure || !Object.keys(structure).length) {
        return structure;
    }
    const normalized = { ...structure };
    if (normalized['CAMERA TEHNICA']) {
        normalized.CT = normalized['CAMERA TEHNICA'];
    } else if (normalized.CT && !normalized['CAMERA TEHNICA']) {
        normalized['CAMERA TEHNICA'] = normalized.CT;
    }
    return normalized;
}

/**
 * Canonical chapter code ("03" → "3", "14" stays "14").
 * @param {unknown} code
 * @returns {string}
 */
export function normalizeChapterCode(code) {
    const raw = String(code ?? '').trim();
    if (!raw) {
        return '';
    }
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * @param {string} system1
 * @returns {Record<string, { name: string, subchapters: Record<string, string> }>}
 */
function getChaptersForSystem(system1) {
    const system = normalizeSystemKey(system1);
    if (!system) {
        return {};
    }
    return getStructureBySystem()[system]?.chapters || {};
}

/**
 * @param {Record<string, { name: string, subchapters: Record<string, string> }>} chapters
 * @param {unknown} chapterCode
 * @returns {{ name: string, subchapters: Record<string, string> }|null}
 */
function getChapterRecord(chapters, chapterCode) {
    if (!chapters) {
        return null;
    }
    const raw = String(chapterCode || '').trim();
    if (raw && chapters[raw]) {
        return chapters[raw];
    }
    const normalized = normalizeChapterCode(raw);
    if (!normalized) {
        return null;
    }
    if (chapters[normalized]) {
        return chapters[normalized];
    }
    for (const [code, chapter] of Object.entries(chapters)) {
        if (normalizeChapterCode(code) === normalized) {
            return chapter;
        }
    }
    return null;
}

/**
 * Deduped chapter list from the full runtime/static structure (all systems).
 * @returns {Array<{ code: string, name: string, systems: string[], subchapters: Record<string, string> }>}
 */
export function listAllChapters() {
    const byCode = new Map();
    const structure = getStructureBySystem();
    for (const [systemKey, systemData] of Object.entries(structure || {})) {
        const system = String(systemKey || '').trim().toUpperCase();
        // CT is an alias of CAMERA TEHNICA — avoid double-counting.
        if (!system || system === 'CT') {
            continue;
        }
        for (const [rawCode, chapter] of Object.entries(systemData?.chapters || {})) {
            const code = normalizeChapterCode(rawCode) || rawCode;
            const existing = byCode.get(code);
            if (!existing) {
                byCode.set(code, {
                    code,
                    name: chapter.name || '',
                    systems: [system],
                    subchapters: { ...(chapter.subchapters || {}) },
                });
                continue;
            }
            if (!existing.systems.includes(system)) {
                existing.systems.push(system);
            }
            existing.subchapters = {
                ...existing.subchapters,
                ...(chapter.subchapters || {}),
            };
            if (!existing.name && chapter.name) {
                existing.name = chapter.name;
            }
        }
    }
    for (const chapter of byCode.values()) {
        chapter.systems.sort();
    }
    return [...byCode.values()].sort((left, right) => Number(left.code) - Number(right.code));
}

/**
 * @param {string} code
 * @returns {{ code: string, name: string, subchapters: Record<string, string> }|null}
 */
export function findChapterByCode(code) {
    const normalized = normalizeChapterCode(code);
    if (!normalized) {
        return null;
    }
    return listAllChapters().find((chapter) => (
        normalizeChapterCode(chapter.code) === normalized
    )) || null;
}

/**
 * Catalog chapter name, or empty when the code has no real label (e.g. "0", "03").
 * @param {unknown} code
 * @returns {string}
 */
export function getChapterDisplayName(code) {
    const normalized = normalizeChapterCode(code);
    const name = String(findChapterByCode(normalized)?.name || '').trim();
    if (
        !name
        || name === normalized
        || name === String(code ?? '').trim()
        || /^\d+$/.test(name)
    ) {
        return '';
    }
    return name;
}

/**
 * Case-insensitive chapter name lookup.
 * @param {string} name
 * @param {{ excludeCode?: string }} [options]
 * @returns {{ code: string, name: string, subchapters: Record<string, string> }|null}
 */
export function findChapterByName(name, options = {}) {
    const normalized = String(name || '').trim().toLocaleLowerCase('ro');
    if (!normalized) {
        return null;
    }
    const excludeCode = String(options.excludeCode || '').trim();
    return listAllChapters().find((chapter) => {
        if (excludeCode && chapter.code === excludeCode) {
            return false;
        }
        return String(chapter.name || '').trim().toLocaleLowerCase('ro') === normalized;
    }) || null;
}

/**
 * @param {string} chapterCode
 * @param {string} subchapterCode
 * @returns {{ code: string, name: string }|null}
 */
export function findSubchapterByCode(chapterCode, subchapterCode) {
    const chapter = findChapterByCode(chapterCode);
    if (!chapter) {
        return null;
    }
    const normalized = padSubchapterCode(subchapterCode);
    if (!normalized || !chapter.subchapters?.[normalized]) {
        return null;
    }
    return {
        code: normalized,
        name: chapter.subchapters[normalized],
    };
}

/**
 * Case-insensitive subchapter name lookup within a chapter.
 * @param {string} chapterCode
 * @param {string} name
 * @param {{ excludeCode?: string }} [options]
 * @returns {{ code: string, name: string }|null}
 */
export function findSubchapterByName(chapterCode, name, options = {}) {
    const chapter = findChapterByCode(chapterCode);
    if (!chapter) {
        return null;
    }
    const normalized = String(name || '').trim().toLocaleLowerCase('ro');
    if (!normalized) {
        return null;
    }
    const excludeCode = padSubchapterCode(options.excludeCode);
    for (const [code, subName] of Object.entries(chapter.subchapters || {})) {
        if (excludeCode && code === excludeCode) {
            continue;
        }
        if (String(subName || '').trim().toLocaleLowerCase('ro') === normalized) {
            return { code, name: subName };
        }
    }
    return null;
}

/**
 * @param {string} system1
 * @returns {Array<{ code: string, name: string, title: string }>}
 */
export function getChapterOptions(system1) {
    const chapters = getChaptersForSystem(system1);
    return Object.entries(chapters)
        .sort(([codeA], [codeB]) => Number(codeA) - Number(codeB))
        .map(([code, chapter]) => ({
            code,
            name: chapter.name,
            title: `${code} – ${chapter.name}`,
        }));
}

/**
 * Union of chapter options across all provided systems (by chapter code).
 * @param {string|string[]} systems
 * @returns {Array<{ code: string, name: string, title: string }>}
 */
export function getChapterOptionsForSystems(systems) {
    const systemList = (Array.isArray(systems) ? systems : [systems])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    if (!systemList.length) {
        return [];
    }
    if (systemList.length === 1) {
        return getChapterOptions(systemList[0]);
    }

    const byCode = new Map();
    for (const system of systemList) {
        for (const option of getChapterOptions(system)) {
            if (!byCode.has(option.code)) {
                byCode.set(option.code, option);
            }
        }
    }
    return [...byCode.values()].sort((left, right) => Number(left.code) - Number(right.code));
}

/**
 * @param {string} system1
 * @param {string} chapterCode
 * @returns {Array<{ code: string, name: string, title: string }>}
 */
export function getSubchapterOptions(system1, chapterCode) {
    const chapter = getChapterRecord(getChaptersForSystem(system1), chapterCode);
    if (!chapter) {
        return [];
    }
    return Object.entries(chapter.subchapters)
        .sort(([codeA], [codeB]) => Number(codeA) - Number(codeB))
        .map(([code, name]) => ({
            code,
            name,
            title: `${code} – ${name}`,
        }));
}

/**
 * Union of subchapter options for a chapter across all provided systems.
 * @param {string|string[]} systems
 * @param {string} chapterCode
 * @returns {Array<{ code: string, name: string, title: string }>}
 */
export function getSubchapterOptionsForSystems(systems, chapterCode) {
    const systemList = (Array.isArray(systems) ? systems : [systems])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    if (!systemList.length) {
        return [];
    }
    if (systemList.length === 1) {
        return getSubchapterOptions(systemList[0], chapterCode);
    }

    const byCode = new Map();
    for (const system of systemList) {
        for (const option of getSubchapterOptions(system, chapterCode)) {
            if (!byCode.has(option.code)) {
                byCode.set(option.code, option);
            }
        }
    }
    return [...byCode.values()].sort((left, right) => Number(left.code) - Number(right.code));
}

/**
 * @param {string} system1
 * @param {string} chapterCode
 * @returns {string}
 */
export function getChapterName(system1, chapterCode) {
    return getChapterRecord(getChaptersForSystem(system1), chapterCode)?.name || '';
}

/**
 * @param {string|string[]} systems
 * @param {string} chapterCode
 * @returns {string}
 */
export function getChapterNameForSystems(systems, chapterCode) {
    const systemList = (Array.isArray(systems) ? systems : [systems])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    for (const system of systemList) {
        const name = getChapterName(system, chapterCode);
        if (name) {
            return name;
        }
    }
    return '';
}

/**
 * @param {string} system1
 * @param {string} chapterCode
 * @param {string} subchapterCode
 * @returns {string}
 */
export function getSubchapterName(system1, chapterCode, subchapterCode) {
    const normalizedSubchapter = padSubchapterCode(subchapterCode);
    return getChapterRecord(getChaptersForSystem(system1), chapterCode)
        ?.subchapters?.[normalizedSubchapter] || '';
}

/**
 * @param {string|string[]} systems
 * @param {string} chapterCode
 * @param {string} subchapterCode
 * @returns {string}
 */
export function getSubchapterNameForSystems(systems, chapterCode, subchapterCode) {
    const systemList = (Array.isArray(systems) ? systems : [systems])
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    for (const system of systemList) {
        const name = getSubchapterName(system, chapterCode, subchapterCode);
        if (name) {
            return name;
        }
    }
    return '';
}

/**
 * @param {string} code
 * @returns {string}
 */
export function padSubchapterCode(code) {
    const normalized = String(code ?? '').trim();
    if (!normalized) {
        return '';
    }
    if (/^\d+$/.test(normalized)) {
        return normalized.padStart(2, '0');
    }
    return normalized;
}
